import { NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { AuthInterceptor } from './service/http/auth-interceptor';
import { DatePipe, registerLocaleData } from '@angular/common';
import { IndexComponent } from './index/index.component';
import { SharedModule } from './module/shared/shared.module';
import { AuthGuard } from './auth.guard';
import {TranslateModule, TranslateLoader, TranslateService} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import localeEn from '@angular/common/locales/en-GB';
import localeEs from '@angular/common/locales/es';
import localeDa from '@angular/common/locales/da';
import localeNl from '@angular/common/locales/nl';
import localeFrBe from '@angular/common/locales/fr-BE';
import localeFrFr from '@angular/common/locales/fr';
import localeFrCa from '@angular/common/locales/fr-CA';
import localeHi from '@angular/common/locales/hi';
import localeIt from '@angular/common/locales/it';
import localeMl from '@angular/common/locales/ms';
import localeRu from '@angular/common/locales/ru';
import localeUk from '@angular/common/locales/uk';

import { TranslateComponent } from './extra/translate/translate.component';
import { StoreModule } from '@ngrx/store';
import { appReducers, REDUCERS_TOKEN } from './store/reducers/app.reducers';
import { EffectsModule } from '@ngrx/effects';
import { MenuEffects } from './store/effects/menu.effects';
import { StoreRouterConnectingModule, DefaultRouterStateSerializer } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NextEffects } from './store/effects/next.effects';
import { RaceEffects } from './store/effects/race.effects';
import { LoginComponent } from './extra/login/login.component';
import { TranslateGuard } from './translate.guard';
import { IdentifyEffects } from './store/effects/identify.effects';
import { LanguagesEffects } from './store/effects/languages.effects';
import { PageEffects } from './store/effects/page.effects';
import { ScoEffects } from './store/effects/sco.effects';
import { VideoComponent } from './extra/video/video.component';
import { VideologinComponent } from './extra/video/videologin/videologin.component';
import { VideoGuard } from './video.guard';
import { BalanceEffects } from './store/effects/balance.effects';
import { VerticalComponent } from './vertical/vertical.component';
import { DeviceDetectorModule } from 'ngx-device-detector';
import { NotTranslatedServiceService } from './service/translate/not-translated-service.service';

import { MissingTranslationHandler, MissingTranslationHandlerParams } from '@ngx-translate/core';
import { RaceResultService } from './service/http/race/raceresult.service';
import { RaceResultEffects } from './store/effects/receresult.effects';
import { BonusPipe } from './pipe/bonus.pipe';
import { TotraceEffects } from './store/effects/totrace.effects';
import { ConsoleLoggerService } from './service/log4ts/console-logger.service';
import { LoggerService } from './service/log4ts/logger.service';
import { TotselectEffects } from './store/effects/totselect.effects';
import { MatPaginatorIntl } from '@angular/material/paginator';
import {CustomMatPaginatorIntl} from './module/material/translate-paginator';
import {TitleCasePipe} from '@angular/common';
import {CookieService} from 'ngx-cookie-service';
import 'hammerjs';
import {Angulartics2Module} from 'angulartics2';
import {EventEffects} from './store/effects/event.effects';
import {HexToDecimalPipe} from './pipe/hex-to-decimal.pipe';
import { NameDottenPipe } from './pipe/name-dotten.pipe';
import {ChannelsEffects} from './store/effects/channels.effects';
import { TestComponent } from './test/test.component';
import {VgBufferingModule} from '@videogular/ngx-videogular/buffering';

export class MyMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    // return this.nts.notTranslated(params.key);

    const Arraykey = params.key.split('.');
    if (Arraykey.length === 2) {
      return Arraykey[1].toLowerCase();
    }
    return params.key;
  }
  constructor(private nts: NotTranslatedServiceService) {}
}

registerLocaleData(localeDa, 'da-DK');
registerLocaleData(localeEn, 'en-GB');
registerLocaleData(localeEs, 'es-ES');
registerLocaleData(localeFrBe, 'fr-BE');
registerLocaleData(localeFrCa, 'fr-CA');
registerLocaleData(localeFrFr, 'fr-FR');
registerLocaleData(localeHi, 'hi-IN');
registerLocaleData(localeIt, 'it-IT');
registerLocaleData(localeMl, 'ml-MY');
registerLocaleData(localeNl, 'nl-BE');
registerLocaleData(localeRu, 'ru-RU');
registerLocaleData(localeUk, 'uk-UA');

// AoT requires an exported function for factories
// export function HttpLoaderFactory(httpClient: HttpClient) {
//  return new TranslateHttpLoader(httpClient,
//    `${environment.apiUrl}/rest/locale/resource/json/5/`);
// }

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(
    httpClient,
    '/assets/i18n/',
    '.json?cacheBuster=' + environment.cacheBusterHash
  );
}

@NgModule({
  declarations: [AppComponent, IndexComponent, TestComponent],
  imports: [
    BrowserAnimationsModule,
    HttpClientModule,
    StoreModule.forRoot(REDUCERS_TOKEN),
    EffectsModule.forRoot([
      MenuEffects,
      NextEffects,
      RaceEffects,
      TotraceEffects,
      IdentifyEffects,
      LanguagesEffects,
      PageEffects,
      ScoEffects,
      TotselectEffects,
      BalanceEffects,
      RaceResultEffects,
      EventEffects,
      ChannelsEffects
    ]),
    StoreRouterConnectingModule.forRoot({ serializer: DefaultRouterStateSerializer, stateKey: 'router' }),
    environment.staging ? StoreDevtoolsModule.instrument() : [],
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MyMissingTranslationHandler,
        deps: [NotTranslatedServiceService],
      },
    }),
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: !environment.staging }),
    SharedModule,
    DeviceDetectorModule.forRoot(),
    Angulartics2Module.forRoot(),
    VgBufferingModule
  ],
  providers: [
    DatePipe,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    AuthGuard,
    { provide: REDUCERS_TOKEN, useValue: appReducers },
    { provide: LoggerService, useClass: ConsoleLoggerService },
    BonusPipe,
    TitleCasePipe,
    CookieService,
    {
      provide: MatPaginatorIntl,
      useFactory: (translate) => {
        const service = new CustomMatPaginatorIntl();
        service.injectTranslateService(translate);
        return service;
      },
      deps: [TranslateService]
    }
  ],
  bootstrap: [AppComponent],
  exports: [],
})
export class AppModule {}
